import {
    useSyncExternalStore,
    createContext,
    useContext,
    useMemo
} from "react";
import type {
    MessageScrollerRootContextType,
    MessageScrollerSnapshotType,
    MessageScrollerContextType,
    MessageScrollerState
} from "./type";

export const MessageScrollerContext = createContext<MessageScrollerContextType | null>(null);

export const MessageScrollerRootContext = createContext<MessageScrollerRootContextType | null>(null);

export const MessageScrollerItemContext = createContext<string | null>(null);

export const useMessageScrollerContext = () => {
    return useContext(MessageScrollerContext);
};

export const useMessageScrollerRootContext = () => {
    return useContext(MessageScrollerRootContext);
};

export const useMessageScrollerSelector = <T, >(
    context: MessageScrollerContextType | null,
    selector: (snapshot: MessageScrollerSnapshotType) => T,
    fallback: T
): T => {
    return useSyncExternalStore(
        (listener) => context ? context.store.subscribe(listener) : () => undefined,
        () => context ? selector(context.store.getSnapshot()) : fallback,
        () => fallback
    );
};

export const useMessageScrollerState = (): MessageScrollerState => {
    const context = useMessageScrollerContext();

    const currentAnchorId = useMessageScrollerSelector(context, (snapshot) => snapshot.currentAnchorId, null);

    const newMessageCount = useMessageScrollerSelector(context, (snapshot) => snapshot.newMessageCount, 0);

    const isAtStart = useMessageScrollerSelector(context, (snapshot) => snapshot.isAtStart, true);

    const isAtEnd = useMessageScrollerSelector(context, (snapshot) => snapshot.isAtEnd, true);

    return useMemo(() => ({
        scrollToMessage: (id, options) => context?.commands.scrollToMessage(id, options) ?? false,
        scrollToStart: () => context?.commands.scrollToStart(),
        scrollToEnd: () => context?.commands.scrollToEnd(),
        currentAnchorId,
        newMessageCount,
        isAtStart,
        isAtEnd
    }), [
        currentAnchorId,
        newMessageCount,
        isAtStart,
        context,
        isAtEnd
    ]);
};
